import { useEffect, useId, useState } from "react";
import { AppSelect } from "../components/inputs/AppSelect";
import { ErrorNotice } from "../components/AdminFeedback";
import { useLocale } from "../i18n";
import { eventRequest, operation } from "./api";
import type { Page, Unit } from "./types";

/** Choosing one round never converts missing/failed catalogue reads into an all-round publication. */
export function PublicationRoundPicker({ unit, value, onChange, onReady, disabled, knownUnits }: {
  unit: Unit; value?: string; onChange: (id: string | undefined) => void;
  onReady: (ready: boolean) => void; disabled?: boolean; knownUnits?: Unit[];
}) {
  const { w } = useLocale(); const id = useId();
  const selectedOnly = value !== undefined;
  const [rounds, setRounds] = useState<Unit[]>([]); const [error, setError] = useState<unknown>();
  const [loaded, setLoaded] = useState(false); const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const controller = new AbortController(); setLoaded(false); setError(undefined); setRounds([]);
    if (!selectedOnly) return () => controller.abort();
    if (knownUnits) { setRounds(knownUnits.filter(u => u.unitKind === "VOTING_ROUND")); setLoaded(true); return () => controller.abort(); }
    void (async () => {
      let total = 0, requests = 0;
      async function children(parentUnitId: string) {
        const rows: Unit[] = []; let cursor = ""; const cursors = new Set<string>();
        do {
          if (cursors.has(cursor) || cursors.size >= 11 || ++requests > 1000) throw new Error("Publication catalogue exceeded pagination bounds"); cursors.add(cursor);
          const page = await eventRequest<Page>(operation("units"), { parentUnitId, limit: 100, ...(cursor ? { cursor } : {}) }, undefined, controller.signal);
          total += page.items.length; if (total > 1000) throw new Error("Publication catalogue exceeds supported size");
          rows.push(...page.items as unknown as Unit[]); cursor = String(page.nextCursor || "");
        } while (cursor);
        return rows;
      }
      const direct = await children(unit.unitId);
      const result = unit.unitKind === "STAGE" ? direct.filter(u => u.unitKind === "VOTING_ROUND") : [];
      if (unit.unitKind === "COMPETITION") for (const season of direct.filter(u => u.unitKind === "STAGE"))
        result.push(...(await children(season.unitId)).filter(u => u.unitKind === "VOTING_ROUND"));
      if (!controller.signal.aborted) { setRounds(result); setLoaded(true); }
    })().catch(failure => { if (!controller.signal.aborted) setError(failure); });
    return () => controller.abort();
  }, [unit.unitId, unit.version, selectedOnly, knownUnits, refresh]);
  const ready = !selectedOnly || (loaded && !error && rounds.some(round => round.unitId === value));
  useEffect(() => { onReady(ready); }, [ready, onReady]);
  return <section className="event-publication-scope" aria-label={w("نطاق النشر", "Publication scope")}>
    <fieldset disabled={disabled}><legend>{w("الجولات المطلوب نشرها", "Rounds to publish")}</legend>
      <label className="check"><input type="radio" name={id} checked={!selectedOnly} onChange={() => onChange(undefined)} />{w("جميع الجولات", "All rounds")}</label>
      <label className="check"><input type="radio" name={id} checked={selectedOnly} onChange={() => onChange("")} />{w("جولة واحدة فقط", "One round only")}</label>
      {selectedOnly && <>
        <ErrorNotice operation="read" error={error} retry={() => setRefresh(n => n + 1)} />
        <label htmlFor={`${id}-round`}>{w("الجولة المطلوب نشرها", "Round to publish")}</label>
        <AppSelect id={`${id}-round`} value={rounds.some(round => round.unitId === value) ? value || "" : ""} onChange={onChange} loading={!loaded && !error} disabled={!loaded || !!error} required>
          <option value="">{w("اختر الجولة", "Choose a round")}</option>
          {rounds.map(round => <option key={round.unitId} value={round.unitId}>{round.title}</option>)}
        </AppSelect>
        {loaded && !error && rounds.length === 0 && <p role="status">{w("لا توجد جولات متاحة في هذا النطاق.", "There are no rounds in this scope.")}</p>}
        {loaded && !error && value && !ready && <p role="alert">{w("الجولة المختارة لم تعد متاحة. راجع الاختيار قبل النشر.", "The selected round is no longer available. Review the selection before publishing.")}</p>}
        <p>{w("سيُنشر هيكل المسابقة والموسم اللازم لهذه الجولة معها. لا يشمل هذا الإجراء نشر الجولات الأخرى ولا يشترط مواعيدها؛ يبقى مجموع أوزان كل موسم 100%.", "The competition and season structure needed for this round will be published with it. Other rounds are not published or required to have dates; every season still needs a 100% weight total.")}</p>
      </>}
    </fieldset>
  </section>;
}
